import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  TaskMutationParams,
  LeaderboardPeriodType,
} from "@/types/challenges.types";
import { usePlausible } from "./usePlausible";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { PulsarService } from "@/services/pulsar.service";
import { toast } from "sonner";
import { getErrorMessage } from "@/helpers/getErrorMessage";
import { consumeTaskGuidAndName } from "@/lib/challenges";
import { useAuth } from "./useAuth";
import { CAMPAIGN_CONFIG } from "@/config";

export const CHALLENGES_KEYS = {
  all: ["challenges"] as const,
  detail: (id: string, groupId: string) => ["challenges", id, groupId] as const,
  byGroup: (group: string) => ["challenges", "group", group] as const,
  taskStatus: (campaignId: string, groupId: string, address: string | undefined, isAuthenticated: boolean) =>
    ["challenges", "task-status", campaignId, groupId, address, isAuthenticated] as const,
  campaignUser: (campaignId: string, groupId: string, address: string | undefined, isAuthenticated: boolean) =>
    ["challenges", "user", campaignId, groupId, address, isAuthenticated] as const,
  campaignLeaderboard: (
    campaignId: string,
    groupId: string,
    limit: number,
    period: LeaderboardPeriodType = LeaderboardPeriodType.ALL
  ) =>
    ["challenges", "leaderboard", campaignId, groupId, limit, period] as const,
  validRevaAccount: (email: string) =>
    ["reva", "valid-account", email] as const,
  referrals: (
    campaignId: string,
    groupId: string,
    address: string,
    limit: number,
    isAuthenticated: boolean
  ) =>
    ["challenges", "referrals", campaignId, groupId, address, limit, isAuthenticated] as const,
};

export const useGetCampaigns = () => {
  return useQuery({
    queryKey: CHALLENGES_KEYS.all,
    queryFn: () => PulsarService.getChallengeCampaigns(),
  });
};

export const useGetAllCampaigns = (
  sortBy?: "participantsAsc" | "participantsDesc"
) => {
  return useQuery({
    queryKey: [CHALLENGES_KEYS.all, "all", sortBy],
    queryFn: async () => {
      const campaigns = await PulsarService.getAllCampaigns();
      const filteredCampaigns = campaigns.filter(
        (campaign: any) => campaign.isEnabled
      );

      if (sortBy === "participantsAsc") {
        return filteredCampaigns.sort(
          (a: any, b: any) => a.totalUsers - b.totalUsers
        );
      }

      if (sortBy === "participantsDesc") {
        return filteredCampaigns.sort(
          (a: any, b: any) => b.totalUsers - a.totalUsers
        );
      }

      const sortedCampaigns = filteredCampaigns
        .sort((a: any, b: any) => {
          const aEndDate = new Date(a.startDate);
          const bEndDate = new Date(b.startDate);
          return bEndDate.getTime() - aEndDate.getTime();
        })
        .filter((campaign: any) => campaign.isEnabled);

      return sortedCampaigns;
    },
  });
};

export const useGetAllCampaignsByGroup = (group: string) => {
  return useQuery({
    queryKey: CHALLENGES_KEYS.byGroup(group),
    queryFn: async () => {
      const campaigns = await PulsarService.getAllCampaignsByGroup(group);
      return campaigns ?? [];
    },
    enabled: !!group,
  });
};

export const useGetCampaign = (id: string, groupId: string | undefined) => {
  return useQuery({
    queryKey: CHALLENGES_KEYS.detail(id, groupId ?? ""),
    queryFn: () => PulsarService.getCampaign(id, groupId),
  });
};
export const useGetCampaignTaskStatus = (
  campaignId: string,
  groupId: string | undefined
) => {
  const { address, isAuthenticated, getAccessToken } = useAuth();

  return useQuery({
    queryKey: CHALLENGES_KEYS.taskStatus(campaignId, groupId || "", address, isAuthenticated),
    queryFn: async () => {
      const token = isAuthenticated ? await getAccessToken() : null;
      return PulsarService.getChallengeCampaignTaskStatus(campaignId, groupId, token);
    },
  });
};

export const useGetCampaignUser = (
  campaignId: string,
  groupId: string | undefined,
  address: string,
  _isAuthenticated: boolean
) => {
  const { getAccessToken } = useAuth();

  return useQuery({
    queryKey: CHALLENGES_KEYS.campaignUser(campaignId, groupId || "", address, _isAuthenticated),
    queryFn: async () => {
      const token = await getAccessToken();
      if (!token) {
        throw new Error("Authentication is still loading.");
      }

      return await PulsarService.getChallengeCampaignUser(campaignId, groupId, token);
    },
    enabled: !!address,
  });
};

export const useGetCampaignLeaderboard = (
  campaignId: string,
  groupId: string | undefined,
  _address: string,
  limit: number = 10,
  period: LeaderboardPeriodType = LeaderboardPeriodType.ALL,
  page: number = 0
) => {
  return useQuery({
    queryKey: [
      ...CHALLENGES_KEYS.campaignLeaderboard(
        campaignId,
        groupId || "",
        limit,
        period
      ),
      page,
    ],
    queryFn: async () => {
      let date;
      if (period === LeaderboardPeriodType.WEEKLY) {
        const today = new Date();
        const dayOfWeek = today.getDay() === 0 ? 7 : today.getDay();
        const diff = 1 - dayOfWeek;
        const monday = new Date(today);
        monday.setDate(today.getDate() + diff);
        date = monday.toISOString();
      }

      return await PulsarService.getChallengeCampaignLeaderboard(
        campaignId,
        groupId,
        page,
        limit,
        period,
        date
      );
    },
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 5,
  });
};

export const useGetReferrals = (
  campaignId: string,
  groupId: string | undefined,
  address: string,
  isAuthenticated: boolean,
  page: number = 1,
  limit: number = 10
) => {
  return useQuery({
    queryKey: [
      ...CHALLENGES_KEYS.referrals(
        campaignId,
        groupId || "",
        address,
        limit,
        isAuthenticated
      ),
      page,
    ],
    queryFn: async () => {
      return await PulsarService.getReferrals(
        campaignId,
        groupId,
        address,
        page,
        limit
      );
    },
    enabled: !!address && isAuthenticated,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 5,
  });
};

export const useQuestsMutations = () => {
  const queryClient = useQueryClient();
  const plausible = usePlausible();
  const { address, isAuthenticated, getAccessToken } = useAuth();

  const taskMutation = useMutation({
    mutationFn: async ({
      taskGuid,
      extraArguments,
      campaignId,
      groupId,
    }: TaskMutationParams) => {
      try {
        const token = await getAccessToken();
        if (!token) {
          throw new Error("Authentication is still loading.");
        }

        const { data: result } = await PulsarService.doCampaignTask(
          taskGuid,
          extraArguments,
          token
        );
        plausible("Airdrop: Do Task", {
          props: { taskGuid },
        });

        if (!result?.status) {
          toast.error(result.error || "Error doing task", {
            duration: 6000,
            dismissible: true,
            style: {
              background: "#fee2e2",
              color: "#991b1b",
              border: "1px solid #fca5a5"
            }
          });
          return null;
        }

        // Immediately fetch the updated task status
        const updatedTaskStatus =
          await PulsarService.getChallengeCampaignTaskStatus(
            campaignId,
            groupId,
            token
          );

        queryClient.setQueryData(
          CHALLENGES_KEYS.taskStatus(
            CAMPAIGN_CONFIG.id,
            CAMPAIGN_CONFIG.groupId,
            address,
            isAuthenticated
          ),
          updatedTaskStatus
        );
        console.log(
          "set data",
          CHALLENGES_KEYS.taskStatus(
            CAMPAIGN_CONFIG.id,
              CAMPAIGN_CONFIG.groupId,
            address,
            isAuthenticated
          ),
          updatedTaskStatus
        );

        return { status: true, updatedTaskStatus, campaignId, groupId };
      } catch (e: any) {
        toast.error(getErrorMessage(e, "Error doing task"), {
          duration: 6000,
          dismissible: true,
          style: {
            background: "#fee2e2",
            color: "#991b1b",
            border: "1px solid #fca5a5"
          }
        });
        return null;
      }
    },
    onSuccess: (_) => {
      // if (data?.updatedTaskStatus) {
      //   // Immediately update the cache with the fetched data
      //   queryClient.setQueryData(
      //     CHALLENGES_KEYS.taskStatus(
      //       data.campaignId,
      //       data.groupId || "",
      //       "",
      //       false
      //     ),
      //     data.updatedTaskStatus
      //   );
      // }

      // Still invalidate queries to ensure consistency
      queryClient.invalidateQueries({
        queryKey: ["challenges", "task-status"],
      });
      queryClient.invalidateQueries({
        queryKey: ["challenges", "user"],
      });
    },
  });

  return {
    taskMutation,
  };
};

export const useConsumeQuestsArgs = (
  campaignId: string,
  groupId: string | undefined
) => {
  const { taskMutation } = useQuestsMutations();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const pathname = location.pathname;

  const getAndConsume = (key: string): string | null => {
    const value = searchParams.get(key);
    if (!value) return null;

    const withoutKey = new URLSearchParams(searchParams.toString());
    withoutKey.delete(key);

    const newUrl = `${pathname}?${withoutKey.toString()}`;
    navigate(newUrl, { replace: true });
    return value;
  };

  const handleConsumeTwitterFollow = async () => {
    const value = getAndConsume("twitterAuth");
    if (!value) return;

    const { taskGuid, taskName } = consumeTaskGuidAndName();
    if (!taskGuid || !taskName) {
      toast.error("Task Guid not found", {
        style: {
          background: "#fee2e2",
          color: "#991b1b",
          border: "1px solid #fca5a5"
        }
      });
      return;
    }

    await taskMutation.mutateAsync({
      campaignId,
      groupId,
      taskName,
      taskGuid,
    });
  };

  const handleConsumeTwitterRelogin = async (
    campaignId: string,
    groupId: string | undefined
  ) => {
    const value = getAndConsume("twitterRelogin");
    if (!value) return;

    const { taskGuid, taskName } = consumeTaskGuidAndName();
    if (!taskGuid || !taskName) {
      toast.error("Task Guid not found", {
        style: {
          background: "#fee2e2",
          color: "#991b1b",
          border: "1px solid #fca5a5"
        }
      });
      return;
    }

    await taskMutation.mutateAsync({
      campaignId,
      groupId,
      taskName,
      taskGuid,
    });
  };

  const handleConsumeDiscordCode = async () => {
    const code = getAndConsume("code");
    if (!code) return;

    const { taskGuid, taskName } = consumeTaskGuidAndName();
    if (!taskGuid || !taskName) {
      toast.error("Task Guid not found", {
        style: {
          background: "#fee2e2",
          color: "#991b1b",
          border: "1px solid #fca5a5"
        }
      });
      return;
    }

    await taskMutation.mutateAsync({
      campaignId,
      groupId,
      taskName,
      extraArguments: [code],
      taskGuid,
    });
  };

  useEffect(() => {
    if (!campaignId) return;

    handleConsumeTwitterFollow();
    handleConsumeDiscordCode();
    handleConsumeTwitterRelogin(campaignId, groupId);
  }, [searchParams, campaignId, groupId]);
};

// Constants for localStorage keys
const REFERRAL_STORAGE_KEY = "pending_referral_code";

export const useReferral = (
  campaignId: string,
  groupId: string | undefined,
  address?: string,
  isAuthenticated: boolean = false
) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const [isOpen, setIsOpen] = useState(false);
  const handleOpen = () => setIsOpen(true);
  const handleClose = () => setIsOpen(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const plausible = usePlausible();

  // Get stored referral code
  const getStoredReferralCode = useCallback(() => {
    if (typeof window === "undefined") return null;
    try {
      const stored = localStorage.getItem(REFERRAL_STORAGE_KEY);
      if (!stored) return null;
      const { code, campaign } = JSON.parse(stored);
      return campaign === campaignId ? code : null;
    } catch {
      return null;
    }
  }, [campaignId]);

  // Store referral code
  const storeReferralCode = useCallback(
    (code: string) => {
      localStorage.setItem(
        REFERRAL_STORAGE_KEY,
        JSON.stringify({ code, campaign: campaignId })
      );
      setReferralCode(code);
    },
    [campaignId]
  );

  // Clear stored referral codechallenges.ts:509:43
  const clearStoredReferralCode = useCallback(() => {
    localStorage.removeItem(REFERRAL_STORAGE_KEY);
    setReferralCode(null);
  }, []);

  // Remove referral code from URL
  const removeReferralFromUrl = useCallback(() => {
    const newSearchParams = new URLSearchParams(searchParams.toString());
    newSearchParams.delete("referralCode");
    const query = newSearchParams.toString();
    const newUrl = query ? `${pathname}?${query}` : pathname;
    navigate(newUrl, { replace: true });
  }, [pathname, navigate, searchParams]);

  // Initialize referral code from URL or localStorage
  useEffect(() => {
    const urlReferralCode = searchParams.get("referralCode");

    if (urlReferralCode) {
      storeReferralCode(urlReferralCode);
      // plausible("Referral: Open", {
      //   props: { referralCode: urlReferralCode },
      // });
    } else {
      const storedCode = getStoredReferralCode();
      if (storedCode) {
        setReferralCode(storedCode);
      }
    }
  }, [searchParams, getStoredReferralCode, storeReferralCode]);

  // Show modal when authenticated and have referral code
  useEffect(() => {
    if (isAuthenticated && address && referralCode) {
      handleOpen();
    }
  }, [isAuthenticated, referralCode, handleOpen]);

  const referralMutation = useMutation({
    mutationFn: async (code: string) => {
      await PulsarService.createReferral(campaignId, groupId, code);

      toast.success("Referral code applied successfully!");
      clearStoredReferralCode();
      removeReferralFromUrl();
      // Invalidate user data to reflect new referral status
      queryClient.invalidateQueries({
        queryKey: CHALLENGES_KEYS.campaignUser(campaignId, groupId || "", address, isAuthenticated),
      });
      queryClient.invalidateQueries({
        queryKey: CHALLENGES_KEYS.taskStatus(campaignId, groupId || "", address, isAuthenticated),
      });
      if (referralCode) {
        plausible("Referral: Apply", {
          props: { referralCode },
        });
      }
      handleClose();
    },
  });

  const handleCloseModal = useCallback(() => {
    removeReferralFromUrl();
    setReferralCode(null);
    clearStoredReferralCode();
    handleClose();
    if (referralCode) {
      plausible("Referral: Close", {
        props: { referralCode },
      });
    }
  }, [removeReferralFromUrl, handleClose, referralCode, plausible]);

  const handleReject = useCallback(() => {
    clearStoredReferralCode();
    removeReferralFromUrl();
    handleClose();
  }, [clearStoredReferralCode, removeReferralFromUrl, handleClose]);

  return {
    isOpen,
    onClose: handleCloseModal,
    onReject: handleReject,
    referralCode,
    applyReferral: referralMutation.mutateAsync,
    isLoading: referralMutation.isPending,
  };
};

/**
 * Fetches user profiles for a list of addresses.
 * Used to enrich leaderboard rows with profile images, names, etc.
 */
export const useGetUserProfiles = (addresses: string[]) => {
  return useQuery({
    queryKey: ["user-profiles", addresses.join(",")],
    queryFn: () => PulsarService.getUserProfiles(addresses),
    enabled: addresses.length > 0,
    staleTime: 1000 * 60 * 10, // 10 min cache
    refetchOnWindowFocus: false,
  });
};
